import React, { useEffect, useState } from "react";
import EventHeader from "../EventHeader";
import VerifyAccModal from "components/Modal/VerifyAccModal";
import { IFinalEvent } from "types/event";
import EventLocation from "../EventLocation";
import { useLang } from "hooks/useLang";
import useWindowSize from "hooks/useWindowSize";
import F1ActionCard from "../f1/components/actionCard/F1ActionCard";
import Sidebar from "components/bookingv2/Sidebar";
import BottomSheetV2 from "components/atoms/BottomSheetV2";

// Grid Components
import { GridLayout, SectionTitle, ContentSection, SliderSection } from "./";

// Event Components
import RenderEventTags from "../f1/components/eventTags/eventTags";
import EventScheduleComponent from "../f1/components/eventSchedule/eventSchedule";
import F1CountDays from "../f1/components/eventSchedule/countDownDays";
import F1GalleryComponent from "../f1/components/gallery/gallery";
import RenderF1WhyComponent from "../f1/components/why/renderWhy";
import JeddahCournicheComponent from "../f1/components/jeddahCourniche/jeddahCourniche";
import RenderF1Ticket from "../f1/components/ticket/F1TicketsCategories";
import RenderDrivers from "../f1/components/drivers/drivers";
import RenderFastFacts from "../f1/components/fastFacts/fastFacts";
import RenderWhatIsFormula from "../f1/components/whatIsFormula/whatIsFormula";
import RenderTeams from "../f1/components/teams/teams";
import FaqComponent from "../f1/components/faq/faq";
import F1Organizer from "../f1/components/organizer/organizer";
import RenderArtiest from "../f1/components/artist/artist";
import RenderVideo from "components/video/renderVideo";

import IntroductionComponent from "../f1/components/introduction/introductionComponent";
import RenderRounds from "../f1/components/rounds/rounds";

interface GridEventContentProps {
  event: IFinalEvent;
  openVerificationModal: boolean;
  setOpenVerificationModal: React.Dispatch<React.SetStateAction<boolean>>;
  isExpiredEvent: boolean;
  showActionCard?: boolean;
  isHeaderFixed?: boolean;
}

const GridEventContent: React.FC<GridEventContentProps> = ({
  event,
  openVerificationModal,
  setOpenVerificationModal,
  isExpiredEvent,
  showActionCard = false,
  isHeaderFixed = false,
}) => {
  const { t } = useLang();
  const { isDesktop } = useWindowSize();
  const [showSideBar, setShowSideBar] = useState(false);
  const [sideBarComponent, setSideBarComponent] = useState();

  const onShowSidebar = () => {
    setShowSideBar((prev) => !prev);
  };

  useEffect(() => {
    const disableScroll = () => {
      document.body.style.overflow = "hidden";
      document.body.style.touchAction = "none"; // Prevents touch gestures (for iOS)
    };

    const enableScroll = () => {
      document.body.style.overflow = "";
      document.body.style.touchAction = ""; // Restores touch gestures
    };

    if (showSideBar) {
      disableScroll();
    } else {
      enableScroll();
    }

    return () => {
      enableScroll();
    };
  }, [showSideBar]);
  
  const galleryImages: string[] = (event?.media?.posters?.gallery || []).map((img) => img.original);
  return (
    <>
      <div className="relative z-10">
        <div className="relative flex flex-col  page-content">
          {/* Header */}
          <div
            className={`w-full mx-auto transition-all duration-300 max-w-screen-xl px-4 md:px-6 lg:px-8
                             ${
                               !isHeaderFixed || !isDesktop
                                 ? ""
                                 : "mb-[10px] pb-[100px]"
                             }`}
          >
            {isDesktop && <div className={"h-[100px]"} />}
            <div
              className={`transition-all duration-300 ease-in-out ${
                isDesktop
                  ? `w-full sticky bottom-0 backdrop-blur-sm
                                  rounded-t-xl shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] z-[30]
                                transition-all duration-300 ease-in-out transform translate-y-0 opacity-100`
                  : "relative"
              }`}
            >
              <EventHeader event={event} />
            </div>
          </div>

          <div className="gap-y-10 flex-col flex w-full h-auto">
            {/* Event Tags Section */}
            <GridLayout
              title={<SectionTitle title="" />}
              content={
                <SliderSection>
                  <RenderEventTags />
                </SliderSection>
              }
            />

            {/* Gallery Section - Standard content in cols 3-4 */}
            <GridLayout
              title={<SectionTitle title="introduction" />}
              content={
                <ContentSection>
                  <IntroductionComponent />
                </ContentSection>
              }
            />

            {/* Schedule Section - Should span cols 3-5 */}
            <GridLayout
              title={<SectionTitle title="schedule" />}
              sliderContent={
                <>
                  <SliderSection>
                    <div className="flex flex-row gap-3 py-1 pb-1">
                      <EventScheduleComponent dayNumber="18" dayName="Friday" />
                      <EventScheduleComponent
                        dayNumber="19"
                        dayName="Saturday"
                      />
                      <EventScheduleComponent dayNumber="20" dayName="Sunday" />
                    </div>
                  </SliderSection>
                  <div className="flex flex-row gap-3 py-1 pb-1">
                    <F1CountDays />
                  </div>
                </>
              }
            />

            {/* Tickets Section - Should span cols 3-5 */}
            <GridLayout
              title={<SectionTitle title="tickets" />}
              sliderContent={
                <SliderSection>
                  <RenderF1Ticket
                    setSideBarComponent={setSideBarComponent}
                    onShowSidebar={() => onShowSidebar()}
                  />
                </SliderSection>
              }
            />

            {/* Gallery Section - Standard content in cols 3-4 */}
            {galleryImages.length > 0 && (
              <GridLayout
                title={<SectionTitle title="gallery" />}
                content={
                  <ContentSection>
                    <F1GalleryComponent images={galleryImages} />
                  </ContentSection>
                }
              />
            )}

            {/* Why This Race is Unmissable Section - Should span cols 3-5 */}
            <GridLayout
              title={<SectionTitle title="videos" />}
              sliderContent={
                <ContentSection>
                  <RenderVideo />
                </ContentSection>
              }
            />

            {/* Location Section - Standard content in cols 3-4 */}
            <GridLayout
              title={<SectionTitle title="location" />}
              content={
                <ContentSection>
                  <div className="w-full flex flex-col p-5 h-[115px] bg-red-50 bg-opacity-30 backdrop-blur-md rounded-[10px] justify-evenly max-w-2xl">
                    <div className="flex flex-row gap-4">
                      <span className="text-4 dark:text-white light:text-[#000] font-bold capitalize">
                        {t("venue")}
                      </span>
                      <span className="text-4 dark:text-white light:text-[#000] font-light">
                        {"Jeddah Corniche Circuit, Jeddah"}
                      </span>
                    </div>
                    <div className="flex flex-row gap-4">
                      <span className="text-4 dark:text-white light:text-[#000] font-bold capitalize">
                        {t("address")}
                      </span>
                      <span className="text-4 dark:text-white light:text-[#000] font-light">
                        {"23512 Corniche Circuit, Jeddah, Saudi Arabia"}
                      </span>
                    </div>
                  </div>
                  <div className="h-5" />
                  <EventLocation event={event} />
                </ContentSection>
              }
            />

            {/* Why This Race is Unmissable Section - Should span cols 3-5 */}
            <GridLayout
              title={<SectionTitle title="WhyThisRaceisUnmissable" />}
              sliderContent={
                <SliderSection>
                  <RenderF1WhyComponent />
                </SliderSection>
              }
            />

            {/* Artists section - Should span cols 3-5 */}
            <GridLayout
              title={<SectionTitle title="artiest" />}
              sliderContent={
                <SliderSection>
                  <RenderArtiest />
                </SliderSection>
              }
            />

            {/* Jeddah Corniche Circuit Section - Standard content in cols 3-4 */}
            <GridLayout
              title={<SectionTitle title="JeddahCornicheCircuit" />}
              content={
                <ContentSection>
                  <JeddahCournicheComponent />
                </ContentSection>
              }
            />

            {/* Fast Facts Section - Should span cols 3-5 */}
            <GridLayout
              title={<SectionTitle title="fastFacts" />}
              sliderContent={
                <SliderSection>
                  <RenderFastFacts />
                </SliderSection>
              }
            />

            {/* What Is Formula Section - Standard content in cols 3-4 */}
            <GridLayout
              title={<SectionTitle title="whatIsFormula" />}
              content={
                <ContentSection>
                  <RenderWhatIsFormula
                    setSideBarComponent={setSideBarComponent}
                    onShowSidebar={() => onShowSidebar()}
                  />
                </ContentSection>
              }
            />

            {/* Rounds section - Standard content in cols 3-4 */}
            <GridLayout
              title={<SectionTitle title="2025RoundsFormula1" />}
              content={
                <SliderSection>
                  <RenderRounds />
                </SliderSection>
              }
            />

            {/* Drivers Section - Should span cols 3-5 */}
            <GridLayout
              title={<SectionTitle title="drivers" />}
              sliderContent={
                <SliderSection>
                  <RenderDrivers
                    setSideBarComponent={setSideBarComponent}
                    onShowSidebar={() => onShowSidebar()}
                  />
                </SliderSection>
              }
            />

            {/* Teams Section - Should span cols 3-5 */}
            <GridLayout
              title={<SectionTitle title="teams" />}
              sliderContent={
                <SliderSection>
                  <RenderTeams />
                </SliderSection>
              }
            />

            {/* FAQ Section - Standard content in cols 3-4 */}
            <GridLayout
              title={<SectionTitle title="faq" />}
              content={
                <ContentSection>
                  <FaqComponent />
                </ContentSection>
              }
            />

            {/* ORG Section - Standard content in cols 3-4 */}
            <GridLayout
              title={<SectionTitle title="organization" />}
              content={
                <ContentSection>
                  <F1Organizer />
                </ContentSection>
              }
            />
          </div>
        </div>
        <div className="h-[20px]" />
        {/* Action Card */}
        <F1ActionCard
          event={event}
          isExpired={isExpiredEvent}
          setOpenVerificationModal={setOpenVerificationModal}
          className={
            !showActionCard && isDesktop ? "translate-y-full opacity-0" : ""
          }
          verificationModalOpen={openVerificationModal}
        />
      </div>

      {/* Verification Modal */}
      <VerifyAccModal
        isModalOpen={openVerificationModal}
        setIsModalOpen={setOpenVerificationModal}
      />

      {/* Sidebar for desktop */}
      <div className="hidden md:block">
        <Sidebar
          className="z-[60]"
          isOpen={showSideBar}
          onClose={() => setShowSideBar(false)}
        >
          {sideBarComponent}
        </Sidebar>
      </div>

      {/* Bottom sheet for mobile */}
      <div className="block md:hidden">
        <BottomSheetV2
          style={{ height: "95%", zIndex: 200 }}
          className="z-[60]"
          isOpen={showSideBar}
          onClose={() => setShowSideBar(false)}
        >
          {sideBarComponent}
        </BottomSheetV2>
      </div>
    </>
  );
};

export default GridEventContent;
